import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate } from 'react-router-dom';
import { BookOpen, Plus, ChevronDown, Filter, GitBranch } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { primaryText, semiBoldText, xxLargeTextSize, whiteBG, borderPrimary, rounded, smallTextSize } from '../../../styling/stylingHelper';
import { useAppUserDefaultStore, isAllRestaurantsSelected } from '../../../stores/appUserDefaultStore';
import { useAuth } from '../../../services/authentication/authContext';
import { canAccessMenuChangeDashboard } from '../../../services/accessManagement/accessManagement';
import { useMenusStore } from '../../../stores/appMenusStore';
import { useCostPriceStore } from '../../../stores/appCostPriceStore';
import { useCostPriceCategoriesStore } from '../../../stores/appCostPriceCategoriesStore';
import { useLoadMenus, useLoadCostPrice, useLoadOrderHistory, useLoadRevenueHistory, useLoadSubrecipes } from '../../../utils/dataLoadingUtils';
import { useSubrecipesStore } from '../../../stores/appSubrecipesStore';
import {
  createMenu as apiCreateMenu,
  updateMenu as apiUpdateMenu,
  deleteMenu as apiDeleteMenu,
  addItemsToMenu as apiAddItemsToMenu,
  removeItemsFromMenu as apiRemoveItemsFromMenu,
} from '../../../services/menus/menusService';
import { Menu } from '../../../services/menus/menusService';
import { useRevenueHistory } from '../../../stores/appRevenueHistoryStore';
import { useOrderHistory } from '../../../stores/appOrderHistoryStore';
import { enrichCostPriceListWithCurrentPrices, getEffectiveMenuPrice, calculateCostPercentage } from '../../../utils/costPriceUtils';
import { useByRestaurantCostData } from '../../../hooks/useByRestaurantCostData';
import { getActiveDishNames } from '../../../utils/costPriceMenuFilter';
import MenuList from './MenuList';
import MenuDetail from './MenuDetail';
import CreateEditMenuModal from './CreateEditMenuModal';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import RestaurantSelector from '../../common/RestaurantSelector';

const MenusPage: React.FC = () => {
  const { t } = useTranslation();
  const { backendUser, accountSettings } = useAuth();
  const canSeeMenuChange = canAccessMenuChangeDashboard(backendUser, accountSettings);

  // Load data
  useLoadMenus();
  useLoadCostPrice();
  useLoadOrderHistory();
  useLoadRevenueHistory();
  useLoadSubrecipes();

  // Stores
  const selectedRestaurant = useAppUserDefaultStore((s) => s.restaurant);
  const restaurantId = selectedRestaurant?.id;
  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);

  const menus = useMenusStore((s) => s.menus);
  const menuItemsMap = useMenusStore((s) => s.menuItems);
  const menusByIdFn = useMenusStore((s) => s.byId);
  const loadMenuItemsFn = useMenusStore((s) => s.loadMenuItems);
  const reloadMenusFn = useMenusStore((s) => s.reload);
  const menusCurrentRestaurantId = useMenusStore((s) => s.currentRestaurantId);
  // Selected menu lives in the store so it survives navigation away & back.
  // The store also clears it when the restaurant changes.
  const selectedMenuId = useMenusStore((s) => s.selectedMenuId);
  const setSelectedMenuId = useMenusStore((s) => s.setSelectedMenuId);
  const costPriceStore = useCostPriceStore();
  const categoriesStore = useCostPriceCategoriesStore();
  const revenueHistoryStore = useRevenueHistory();
  const orderHistoryStore = useOrderHistory();
  const subrecipesById = useSubrecipesStore(s => s.subrecipesById);
  const showExVatOrders = useAppUserDefaultStore(s => s.showExVatOrders);

  // Local state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingMenu, setEditingMenu] = useState<Menu | null>(null);
  const [confirmDeleteMenu, setConfirmDeleteMenu] = useState<Menu | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const statusFilterRef = useRef<HTMLDivElement>(null);

  // Close filter dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target as Node)) {
        setIsStatusFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedMenu = selectedMenuId ? menusByIdFn(selectedMenuId) : null;
  const menuItemIds = useMemo(
    () => (selectedMenuId ? menuItemsMap[selectedMenuId] || [] : []),
    [selectedMenuId, menuItemsMap]
  );

  // Filter menus by status, then surface active menus above inactive ones.
  // Array.prototype.sort is stable, so the relative order within each group is preserved.
  const filteredMenus = menus
    .filter((menu) => {
      if (statusFilter === 'all') return true;
      if (statusFilter === 'active') return menu.isActive;
      return !menu.isActive;
    })
    .sort((a, b) => Number(b.isActive) - Number(a.isActive));

  // Counts derived from the store so they update once items finish loading.
  const menuItemCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const menu of menus) {
      counts[menu.id] = menuItemsMap[menu.id]?.length || 0;
    }
    return counts;
  }, [menus, menuItemsMap]);

  // Safety net: ensure items are loaded for the selected menu (store load also eagerly loads all).
  useEffect(() => {
    if (selectedMenuId) {
      loadMenuItemsFn(selectedMenuId);
    }
  }, [selectedMenuId, loadMenuItemsFn]);

  // Auto-select the top menu (first active one, since filteredMenus is sorted
  // active-first) when nothing is currently selected. Because selectedMenuId
  // lives in the store, this fires only on a fresh visit / restaurant switch —
  // not when re-navigating back to the page, which preserves the previous pick.
  // Skipped while menus are still loading for this restaurant to avoid
  // selecting from a stale (other-restaurant) menu list.
  useEffect(() => {
    if (!restaurantId || isAllRestaurants) return;
    if (menusCurrentRestaurantId !== restaurantId) return;
    if (selectedMenuId) return;
    if (filteredMenus.length === 0) return;

    setSelectedMenuId(filteredMenus[0].id);
  }, [restaurantId, isAllRestaurants, menusCurrentRestaurantId, filteredMenus, selectedMenuId, setSelectedMenuId]);

  // Cross-restaurant sourcing: load referenced siblings so borrowed ingredients
  // price from their source restaurant (otherwise they'd show €0 cost here too).
  const byRestaurant = useByRestaurantCostData(costPriceStore.costPriceList, restaurantId);

  // Enrich cost price data with current prices from order history.
  // Pass revenueHistory so BYO dishes carry their weighted breakdown into menu views.
  const enrichedCostPriceList = useMemo(() =>
    enrichCostPriceListWithCurrentPrices(
      costPriceStore.costPriceList,
      orderHistoryStore.orderHistoryList,
      {
        customIngredients: costPriceStore.customIngredients,
        showExVat: showExVatOrders,
        allSubrecipes: subrecipesById,
        revenueHistory: revenueHistoryStore.revenueHistoryList,
        byRestaurant,
        currentRestaurantId: restaurantId,
      }
    ),
    [costPriceStore.costPriceList, orderHistoryStore.orderHistoryList, costPriceStore.customIngredients, showExVatOrders, subrecipesById, revenueHistoryStore.revenueHistoryList, byRestaurant, restaurantId]
  );

  // Get enriched CostPriceAnalysis objects for menu items
  const menuCostPriceItems = enrichedCostPriceList.filter((item) =>
    menuItemIds.includes(item.id)
  );

  // Build a price & margin map for menu items
  const itemPriceMap = useMemo(() => {
    const map: Record<string, { price: number; margin: number }> = {};
    menuCostPriceItems.forEach((item) => {
      const effectivePrice = getEffectiveMenuPrice(item, revenueHistoryStore.revenueHistoryList);
      const menuPrice = effectivePrice ? effectivePrice.menuPrice : 0;
      const costPct = calculateCostPercentage(item.totalCost, menuPrice);
      const margin = (menuPrice > 0 && item.totalCost > 0) ? 100 - costPct : 0;
      map[item.id] = { price: menuPrice, margin };
    });
    return map;
  }, [menuCostPriceItems, revenueHistoryStore.revenueHistoryList]);

  // Names of revenue items sold in the last 30 days — used to power "Select active" in the add-items tab.
  const activeDishNames = useMemo(
    () => getActiveDishNames(revenueHistoryStore.revenueHistoryList),
    [revenueHistoryStore.revenueHistoryList]
  );

  // Handlers
  const handleCreateMenu = async (name: string, isActive: boolean) => {
    if (!restaurantId) return;
    await apiCreateMenu({ restaurantId, name, isActive });
    await reloadMenusFn();
  };

  const handleEditMenu = async (name: string, isActive: boolean) => {
    if (!editingMenu) return;
    await apiUpdateMenu({ id: editingMenu.id, name, isActive });
    await reloadMenusFn();
    setEditingMenu(null);
  };

  const handleDeleteMenu = async () => {
    if (!confirmDeleteMenu) return;
    await apiDeleteMenu(confirmDeleteMenu.id);
    if (selectedMenuId === confirmDeleteMenu.id) {
      setSelectedMenuId(null);
    }
    setConfirmDeleteMenu(null);
    await reloadMenusFn();
  };

  const handleToggleActive = async (menu: Menu) => {
    await apiUpdateMenu({ id: menu.id, isActive: !menu.isActive });
    await reloadMenusFn();
  };

  const handleAddItems = async (costPriceIds: string[]) => {
    if (!selectedMenuId) return;
    await apiAddItemsToMenu(selectedMenuId, costPriceIds);
    await loadMenuItemsFn(selectedMenuId);
  };

  const handleRemoveItem = async (costPriceId: string) => {
    if (!selectedMenuId) return;
    await apiRemoveItemsFromMenu(selectedMenuId, [costPriceId]);
    await loadMenuItemsFn(selectedMenuId);
  };

  const handleRemoveItems = async (costPriceIds: string[]) => {
    if (!selectedMenuId) return;
    await apiRemoveItemsFromMenu(selectedMenuId, costPriceIds);
    await loadMenuItemsFn(selectedMenuId);
  };

  // Change Dashboard entry point. Only rendered on the per-restaurant view (the
  // all-restaurants case redirects below), so it goes straight to that
  // restaurant's timeline rather than the cross-restaurant overview.
  const changeDashboardLink = (
    <Link
      to={restaurantId ? `/menus/change-dashboard/${restaurantId}` : '/menus/change-dashboard'}
      className={cn(
        "flex items-center justify-center gap-2 text-nowrap rounded-xl px-[13.5px] h-11",
        "text-sm font-semibold border",
        borderPrimary,
        primaryText,
        "hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors flex-shrink-0"
      )}
    >
      <GitBranch className="w-4 h-4" />
      <span className="text-[16px] leading-6 font-semibold">{t('menuChange.changeDashboard')}</span>
    </Link>
  );

  // Menus themselves are per-restaurant, so "All restaurants" has nothing to
  // show here. The change dashboard IS cross-restaurant, so anyone with that
  // permission goes straight there instead of hitting a dead-end warning.
  if (isAllRestaurants && canSeeMenuChange) {
    return <Navigate to="/menus/change-dashboard" replace />;
  }

  if (isAllRestaurants) {
    return (
      <div>
        <MobilePageHeader title={t('menus.title')} right={<RestaurantSelector />} />
        <div className="w-full hidden lg:flex items-center justify-between gap-3 pb-6">
          <h2 className={cn(xxLargeTextSize, semiBoldText, primaryText)}>
            {t('menus.title')}
          </h2>
        </div>
        <div className="bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
          <p className="text-yellow-800 dark:text-yellow-200">
            {t('costPrice.create.pleaseSelectSpecificRestaurant')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <MobilePageHeader title={t('menus.title')} right={<RestaurantSelector />} />

      {/* Page header with title, filter and create button */}
      <div className="w-full flex flex-col gap-4 min-[1050px]:flex-row min-[1050px]:justify-between min-[1050px]:items-center pb-6">
        <h2 className={cn(xxLargeTextSize, semiBoldText, primaryText, 'hidden lg:block')}>
          {t('menus.title')}
        </h2>

        <div className="flex max-lg:flex-wrap items-center gap-3">
          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-zinc-400 dark:text-zinc-500" />
            <div className="relative" ref={statusFilterRef}>
              <button
                onClick={() => setIsStatusFilterOpen(!isStatusFilterOpen)}
                className={cn(
                  "flex items-center gap-2 px-3 py-2",
                  whiteBG,
                  borderPrimary,
                  rounded,
                  smallTextSize,
                  semiBoldText,
                  "hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                )}
              >
                <span className={primaryText}>
                  {statusFilter === 'all' ? t('menus.filterAll') : statusFilter === 'active' ? t('menus.active') : t('menus.inactive')}
                </span>
                <ChevronDown className="w-4 h-4 text-zinc-400 dark:text-zinc-500" />
              </button>

              {isStatusFilterOpen && (
                <div className={cn(
                  "absolute top-full right-0 mt-1 w-40 z-50 shadow-lg",
                  whiteBG,
                  borderPrimary,
                  rounded
                )}>
                  <div className="p-2 space-y-1">
                    {(['all', 'active', 'inactive'] as const).map((status) => (
                      <button
                        key={status}
                        onClick={() => {
                          setStatusFilter(status);
                          setIsStatusFilterOpen(false);
                        }}
                        className={cn(
                          "w-full text-left px-3 py-2 rounded",
                          smallTextSize,
                          statusFilter === status
                            ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
                            : "hover:bg-zinc-100 dark:hover:bg-zinc-800",
                          primaryText
                        )}
                      >
                        {status === 'all' ? t('menus.filterAll') : status === 'active' ? t('menus.active') : t('menus.inactive')}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Change Dashboard button (per-account menu_change_dashboard flag) */}
          {canSeeMenuChange && changeDashboardLink}

          {/* Create Menu button */}
          <button
            onClick={() => setShowCreateModal(true)}
            className={cn(
              "flex items-center justify-center gap-2 text-nowrap rounded-xl px-[13.5px] h-11",
              "text-sm font-semibold bg-[#0E8965] dark:bg-[#0E8965] text-white dark:text-white",
              "hover:bg-[#047857] transition-colors flex-shrink-0"
            )}
          >
            <Plus className="w-5 h-5" />
            <span className="text-white dark:text-white text-[16px] leading-6 font-semibold">{t('menus.createMenu')}</span>
          </button>
        </div>
      </div>

      {/* Two-panel layout in a white card — stacks vertically on mobile, side-by-side at ≥lg */}
      <div className="flex flex-col lg:flex-row bg-white dark:bg-background-dark rounded-xl border border-transparent dark:border-borderPrimary-dark overflow-hidden lg:h-[calc(100vh-200px)] lg:min-h-[500px]">
        {/* Left panel - Menu List */}
        <div className="w-full lg:w-[320px] flex-shrink-0 border-b lg:border-b-0 lg:border-r border-zinc-200 dark:border-borderPrimary-dark">
          <MenuList
            menus={filteredMenus}
            selectedMenuId={selectedMenuId}
            menuItemCounts={menuItemCounts}
            onSelect={setSelectedMenuId}
            onEdit={(menu) => setEditingMenu(menu)}
            onDelete={(menu) => setConfirmDeleteMenu(menu)}
            onToggleActive={handleToggleActive}
          />
        </div>

        {/* Right panel - Menu Detail */}
        <div className="flex-1 min-w-0">
          {selectedMenu ? (
            <MenuDetail
              menu={selectedMenu}
              items={menuCostPriceItems}
              allCostPriceItems={enrichedCostPriceList}
              menuItemIds={menuItemIds}
              categories={categoriesStore.categories}
              itemPriceMap={itemPriceMap}
              activeDishNames={activeDishNames}
              onAddItems={handleAddItems}
              onRemoveItem={handleRemoveItem}
              onRemoveItems={handleRemoveItems}
              onToggleActive={() => handleToggleActive(selectedMenu)}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-zinc-400 dark:text-zinc-500">
              <BookOpen className="w-16 h-16 mb-4" />
              <p className="text-sm">{t('menus.selectMenu')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Menu Modal */}
      {showCreateModal && (
        <CreateEditMenuModal
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateMenu}
        />
      )}

      {/* Edit Menu Modal */}
      {editingMenu && (
        <CreateEditMenuModal
          menu={editingMenu}
          onClose={() => setEditingMenu(null)}
          onSave={handleEditMenu}
        />
      )}

      {/* Delete Confirmation */}
      {confirmDeleteMenu && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/50" onClick={() => setConfirmDeleteMenu(null)}>
          <div
            className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 mb-2">
              {t('menus.deleteMenu')}
            </h3>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-6">
              {t('menus.confirmDelete')}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmDeleteMenu(null)}
                className="px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-800 rounded-xl hover:bg-zinc-200 dark:hover:bg-zinc-700"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleDeleteMenu}
                className="px-4 py-2 text-sm font-medium text-white dark:text-white bg-red-600 dark:bg-red-600 rounded-xl hover:bg-red-700"
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenusPage;
